import createClient from "openapi-fetch";
import { type paths } from "@njbsoft/types";
import { type RefreshManager } from "./refresh-manager";

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  refreshManager?: RefreshManager;
  onUnauthorized?: () => void;
}

// Paths where 401 should NOT trigger a refresh attempt.
//
// The two verify-email ops belong here for a different reason than the rest:
// they are ANONYMOUS, reached from a mailed link, and their 401 is the verdict
// on the TOKEN in that link ("invalid or expired"), never on a session. Left
// out, an expired link's 401 ran the refresh-and-replay — which on a visitor
// with no session ends in the teardown and a hard navigation to /login, so the
// landing page unmounted before it could render the message the caller needs.
const AUTH_PATHS = [
  "/auth/login",
  "/auth/signup",
  "/auth/refresh",
  "/auth/logout",
  "/auth/mfa/challenge",
  "/auth/mfa/verify",
  "/auth/verify-email",
  "/auth/verify-account-email",
];

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.some((p) => url.includes(p));
}

// The PUBLIC, Turnstile-gated invite ops on the customer stack. They are
// `security: []` routes reached from a mailed link with no session.
//
// Anchored on `/customer/` and matched against the URL's PATH, never as a bare
// substring: the main stack's authenticated utility ops
// (`/account-invites/{inviteId}/resend`, `/accounts/{accountId}/invites`) sit
// one segment apart from these and MUST keep their ordinary behaviour.
const PUBLIC_INVITE_PATHS = [
  "/customer/account-invites/preview",
  "/customer/account-invites/accept",
  "/customer/account-invites/resend",
];

/**
 * True for the public invite ops, which are never replayed on 429 OR 401.
 *
 * The carrier check below catches these while a Turnstile site key is
 * configured, because the challenge is then on the request. With the gate OFF
 * — local dev, E2E, and any deployment without a site key — the preview is a
 * bare GET that the repeatability rule would happily replay, so a rate-limited
 * visitor sat on the loading card for the whole `Retry-After` sleep instead of
 * seeing the refusal. The path is the invariant the header is not.
 *
 * On 401 the same exclusion holds, and NOT because a stale bearer is
 * impossible here: the accept is mounted behind an optional bearer that fails
 * closed, so an expired access token on it answers 401 exactly as a refused
 * invite does. Either way the exit is the customer client's `onUnauthorized`
 * teardown back to the invite link, never a refresh-and-replay — the replay
 * would re-send a challenge already spent.
 */
function isPublicInvitePath(url: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return false;
  }
  return PUBLIC_INVITE_PATHS.some((p) => pathname.endsWith(p));
}

/**
 * True when the request carries a single-use Turnstile challenge.
 *
 * A challenge rides one of two carriers, and checking only the first is how a
 * spent response gets replayed anyway: the `X-Turnstile-Token` HEADER on reads
 * (a GET has no body to put it in), and a `turnstile_token` field in the JSON
 * BODY on the invite accept/resend POSTs. Reading the body is why this is
 * async; cloning is why it is safe — the caller keeps its own unconsumed clone
 * to replay with if the answer is no.
 *
 * An unreadable body answers YES. A wrong "no" here is precisely the replay
 * the guard exists to prevent, and a request that cannot be inspected cannot
 * be proven challenge-free.
 *
 * This is a property OF THE REQUEST on purpose. A per-call-site opt-out is a
 * flag the next hook forgets to set; siteverify redeems a response on first
 * use no matter which hook sent it.
 */
async function carriesTurnstileChallenge(request: Request): Promise<boolean> {
  if (request.headers.has("X-Turnstile-Token")) return true;

  const method = request.method.toUpperCase();
  if (method === "GET" || method === "HEAD") return false;

  let raw: string;
  try {
    raw = await request.clone().text();
  } catch {
    return true;
  }
  try {
    const body: unknown = JSON.parse(raw);
    return typeof body === "object" && body !== null && "turnstile_token" in body;
  } catch {
    // A body that is not JSON cannot carry the JSON field.
    return false;
  }
}

// A 429 replay is only safe when the request is repeatable. A mutating method
// cannot be re-sent blind — the first attempt may have landed — and a request
// carrying a Turnstile response cannot be re-sent at all: siteverify redeems
// the response on first use, so the replay answers "verification failed"
// instead of the rate-limit refusal the caller has to see and act on. The
// method gate covers the body carrier too: every request that can hold a
// `turnstile_token` field is a POST, and no POST is replayed here.
//
// The public invite ops are excluded by PATH on top of that, because with no
// Turnstile site key configured the preview carries no challenge to detect and
// would otherwise qualify as an ordinary repeatable GET.
function isReplayableOn429(request: Request): boolean {
  if (isPublicInvitePath(request.url)) return false;
  const method = request.method.toUpperCase();
  if (method !== "GET" && method !== "HEAD") return false;
  return !request.headers.has("X-Turnstile-Token");
}

/**
 * Gives a bodiless 4xx/5xx the error envelope the API itself sends.
 *
 * openapi-fetch leaves `error` empty when a failed response has no body (it
 * reads the empty text, or skips the read on `Content-Length: 0`), so every
 * `if (error)` guard in the portals would read a bodiless 502 / 504 / 429 from
 * a gateway as a success. A response with a body passes through untouched and
 * unconsumed — the probe reads a clone.
 *
 * Only 4xx/5xx qualify: a 304 is a null-body status the Response constructor
 * refuses a body for, and the catalog snapshot reads its 304 by status.
 */
async function withErrorBody(response: Response): Promise<Response> {
  if (response.status < 400) return response;
  if (response.headers.get("Content-Length") !== "0" && (await response.clone().text()) !== "") {
    return response;
  }
  const headers = new Headers(response.headers);
  // A `Content-Length: 0` copied over would make openapi-fetch skip the new body.
  headers.delete("Content-Length");
  headers.set("Content-Type", "application/json");
  return new Response(
    JSON.stringify({ error: response.statusText || String(response.status), code: "" }),
    { status: response.status, statusText: response.statusText, headers },
  );
}

// Paths-generic core shared by every spec-specific factory (main + field).
// The auth middleware (token injection, 401 refresh replay, and a 429 retry
// limited to repeatable requests) is spec-agnostic; only the openapi-fetch
// paths type varies per stack.
export function buildClient<Paths extends object>(options: ApiClientOptions) {
  const { baseUrl, getToken, refreshManager, onUnauthorized } = options;

  const client = createClient<Paths>({ baseUrl, credentials: "include" });

  // Registered ahead of the auth middleware so its onResponse runs AFTER that
  // one's (openapi-fetch unwinds onResponse in reverse): the 429 and 401 arms
  // still see the original response, and a response they replay is covered too.
  client.use({ onResponse: ({ response }) => withErrorBody(response) });

  // Per-request clone map: each request gets its own clone so concurrent 401
  // retries replay the correct body. WeakMap ensures clones are GC'd when the
  // original request is no longer referenced.
  const requestClones = new WeakMap<Request, Request>();

  client.use({
    async onRequest({ request }) {
      // If a refresh is in flight, wait for it before attaching the token
      if (refreshManager?.isRefreshing) {
        await refreshManager.refresh();
      }

      // Read token fresh (may have been updated by the refresh above)
      const token = getToken();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      // Save a clone BEFORE fetch consumes the body stream.
      // This clone preserves the full body for POST/PUT/PATCH retry.
      requestClones.set(request, request.clone());
      return request;
    },
    async onResponse({ request, response }) {
      // Handle 429 rate limiting: single transparent retry if Retry-After <= 60s,
      // and only for a repeatable request
      if (response.status === 429) {
        if (!isReplayableOn429(request)) return response;
        const retryAfter = response.headers.get("Retry-After");
        if (retryAfter) {
          const seconds = parseInt(retryAfter, 10);
          if (!isNaN(seconds) && seconds <= 60) {
            await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
            const clone = requestClones.get(request);
            if (clone) {
              const token = getToken();
              if (token) {
                clone.headers.set("Authorization", `Bearer ${token}`);
              }
              return fetch(clone);
            }
          }
        }
        return response;
      }

      if (response.status !== 401) return response;

      // Do not attempt refresh for auth endpoints
      if (isAuthPath(request.url)) return response;

      // Two kinds of request are never refresh-and-replayed here.
      //
      // A public invite op, by PATH: this is the only arm that fires when no
      // Turnstile site key is configured and the request therefore carries no
      // challenge to detect. Its 401 is not necessarily a verdict on the
      // invite — the accept sits behind an optional bearer that fails closed,
      // so an expired access token answers 401 there too — and neither reading
      // is refreshed and replayed.
      //
      // Anything carrying a Turnstile challenge, by CARRIER: the invite accept
      // verifies Turnstile FIRST and can answer 401 afterwards (a bearer whose
      // profile is no longer live), so by the time the replay went out the
      // challenge was already redeemed and the server answered 403
      // turnstile_failed — burying the 401 the caller has to act on.
      //
      // The refresh is skipped along with the replay: rotating tokens without
      // re-sending leaves a half-signed-in session behind a request that never
      // landed. Surfacing the 401 to onUnauthorized is the designed teardown.
      if (
        isPublicInvitePath(request.url) ||
        (await carriesTurnstileChallenge(requestClones.get(request) ?? request))
      ) {
        onUnauthorized?.();
        return response;
      }

      // Attempt silent token refresh via RefreshManager
      if (refreshManager) {
        const result = await refreshManager.refreshOutcome();
        if (result.outcome === "refreshed") {
          // Retry the original request using the clone (which has an unconsumed body)
          const clone = requestClones.get(request);
          if (clone) {
            const freshToken = getToken();
            if (freshToken) {
              clone.headers.set("Authorization", `Bearer ${freshToken}`);
            }
            return fetch(clone);
          }
          // No clone to replay: fall through to onUnauthorized, as before.
        } else if (result.outcome === "offline") {
          // A transport-failed refresh cannot tell a revoked session from a dead
          // network. Return the original 401 untouched: never a stale-token
          // replay, never a sign-out. Offline capture keeps its session.
          return response;
        }
        onUnauthorized?.();
        return response;
      }

      // No refreshManager configured
      onUnauthorized?.();
      return response;
    },
  });

  return client;
}

export function createApiClient(options: ApiClientOptions): ReturnType<typeof createClient<paths>>;
export function createApiClient(
  baseUrl: string,
  getToken: () => string | null,
): ReturnType<typeof createClient<paths>>;
export function createApiClient(
  baseUrlOrOptions: string | ApiClientOptions,
  getTokenArg?: () => string | null,
) {
  const options: ApiClientOptions =
    typeof baseUrlOrOptions === "string"
      ? { baseUrl: baseUrlOrOptions, getToken: getTokenArg ?? (() => null) }
      : baseUrlOrOptions;

  return buildClient<paths>(options);
}

export type ApiClient = ReturnType<typeof createApiClient>;
